import { requestCalendarScrollToTime } from "@/components/canvas/useCanvasCalendarNav.ts";
import {
  moveSharedCalendar,
  readCalendarWindow,
} from "@/components/canvas/useSharedCalendarState.ts";
import { parseISO } from "date-fns";
import { type CalendarLinkParts, linkRangeToInstants } from "./calendar-url.ts";
import type { TimeRange } from "./time-range-selection.ts";
import { replaceCalendarRanges } from "./useCalendarSelection.ts";

/**
 * Converts `/calendar?r=` link ranges into `TimeRange` selections. Each entry's
 * ISO instants are resolved via `linkRangeToInstants` so the midnight-end
 * convention is respected (see calendar-url.ts).
 */
export function calendarLinkRangesToTimeRanges(
  ranges: NonNullable<CalendarLinkParts["ranges"]>,
  timezone: string,
): TimeRange[] {
  return ranges.map((r) => {
    const { startISO, endISO } = linkRangeToInstants(r, timezone);
    return { id: `${startISO}__${endISO}`, startISO, endISO, allDay: false };
  });
}

/**
 * Whether a calendar link actually asks for anything. A bare `/calendar` carries
 * no focus, so there is nothing to apply in place — the boundary lets it through
 * to load the page normally.
 */
export function hasCalendarFocus(parts: CalendarLinkParts) {
  return parts.date !== undefined || (parts.ranges?.length ?? 0) > 0;
}

/**
 * Point the shared calendar at what a `/calendar?…` link asks for: its day, its
 * view, and its highlighted ranges. Both stores are global, so this needs no
 * handle on the mounted surface — whichever calendar is on screen follows.
 *
 * Idempotent by construction: re-applying the same link writes the same state,
 * which is why clicking a link a second time still re-seeds a selection the user
 * has since cleared.
 */
export function applyCalendarFocus(parts: CalendarLinkParts, timezone: string) {
  const ranges = parts.ranges ? calendarLinkRangesToTimeRanges(parts.ranges, timezone) : undefined;
  // A ranges-only link (the agent's day-range links carry no `date=`) opens on
  // the first range's day rather than leaving the user wherever they were.
  const date = parts.date ?? parts.ranges?.[0]?.date;

  moveSharedCalendar({
    date: date ? parseISO(date) : undefined,
    // An explicit `view=` wins. Otherwise a range would land inside a month cell,
    // where a highlight is too small to read — so ranges force a time grid, while
    // a date-only link leaves the user's chosen view alone.
    view:
      parts.view ??
      (ranges && ranges.length > 0 && readCalendarWindow().viewType === "month"
        ? "day"
        : undefined),
  });

  // Only touch the selection when the link actually carries ranges: a date-only
  // link moves the calendar without discarding a selection the user is holding.
  if (!ranges) return;
  replaceCalendarRanges(ranges);

  // Land the grid on the range instead of wherever the day happened to be
  // scrolled — a 2pm range is off-screen on a grid resting at 8am, so the
  // highlight would be invisible on arrival. Earliest start wins for a
  // multi-range link; month view ignores it (no time axis to scroll).
  const earliest = parts.ranges?.reduce(
    (min, r) => (r.start < min ? r.start : min),
    parts.ranges[0].start,
  );
  if (earliest) requestCalendarScrollToTime(earliest);
}
